import { DispatchAreaOption } from '@mark43/cad-api';
import React, { useEffect, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { useBootstrapState } from '../bootstrap/bootstrap-provider';
import { useBroadcast } from '../common/broadcast-channel/use-broadcast';
import { useApplicationType } from '../common/hooks/use-application-type';
import { useDispatchAreaStore } from '../dispatch-areas/use-dispatch-areas';
import { LocationBiasContext } from './location-bias-context';
import { useLocationBiasStorage } from './use-location-bias-storage';

export const LocationBiasProvider = observer(
    (props: { children: React.ReactElement }): React.ReactElement => {
        const bootstrapState = useBootstrapState();
        const { isFirstResponder, isCadElectron } = useApplicationType();
        const dispatchAreaStore = useDispatchAreaStore();
        const [locationBias, setLocationBias] = useLocationBiasStorage();

        const dispatchAreaOptions: DispatchAreaOption[] = useMemo(() => {
            return (isFirstResponder
                ? dispatchAreaStore.allDispatchAreaObjects
                : dispatchAreaStore.monitoredAndOwnedDispatchAreaObjects
            )
                .filter((dArea) => !!dArea.id)
                .map((dArea) => ({
                    value: dArea.id!,
                    label: dArea.name,
                }))
                .sort((a, b) => a.label.localeCompare(b.label));
        }, [
            isFirstResponder,
            dispatchAreaStore.allDispatchAreaObjects,
            dispatchAreaStore.monitoredAndOwnedDispatchAreaObjects,
        ]);

        const { postMessage } = useBroadcast('change-location-bias', (message) => {
            if (message.type === 'LOCATION_BIAS_CHANGED') {
                const dispatchArea = dispatchAreaOptions.find(
                    (dispatchAreaOption) => dispatchAreaOption.value === message.payload
                );
                setLocationBias(dispatchArea);
            }
        });

        const value = useMemo(() => {
            return {
                dispatchAreaOptions,
                locationBias,
                setLocationBiasId: (dispatchAreaId?: number) => {
                    const dispatchArea = dispatchAreaId
                        ? dispatchAreaOptions.find(
                              (dispatchAreaOption) => dispatchAreaOption.value === dispatchAreaId
                          )
                        : undefined;
                    setLocationBias(dispatchArea);
                    if (isCadElectron) {
                        postMessage({
                            type: 'LOCATION_BIAS_CHANGED',
                            payload: dispatchArea?.value,
                        });
                    }
                },
            };
        }, [dispatchAreaOptions, locationBias, setLocationBias, isCadElectron, postMessage]);

        useEffect(
            function updateDispatchLocationBiasOnMonitoringDispatchAreasChange() {
                if (!isFirstResponder) {
                    const matchedDispatchAreaForLocationBiasId = dispatchAreaOptions.find(
                        (dispatchAreaOption) => dispatchAreaOption.value === locationBias?.value
                    );
                    setLocationBias(matchedDispatchAreaForLocationBiasId);
                }
            },
            [isFirstResponder, dispatchAreaOptions, locationBias?.value, setLocationBias]
        );

        useEffect(
            function updateFirstResponderLocationBiasOnUnitDispatchAreaChange() {
                if (isFirstResponder) {
                    if (!bootstrapState.userAssignmentBundle?.unitDispatchAreaId) {
                        setLocationBias(undefined);
                    } else {
                        const dispatchArea = dispatchAreaOptions.find(
                            (dispatchAreaOption) =>
                                dispatchAreaOption.value ===
                                bootstrapState.userAssignmentBundle?.unitDispatchAreaId
                        );
                        setLocationBias(dispatchArea);
                    }
                }
            },
            [
                isFirstResponder,
                dispatchAreaOptions,
                bootstrapState.userAssignmentBundle?.unitDispatchAreaId,
                setLocationBias,
            ]
        );

        return (
            <LocationBiasContext.Provider value={value}>
                {props.children}
            </LocationBiasContext.Provider>
        );
    }
);
